import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseDialogFormikHandler,
  CloseFormikDialogResult,
  Dialog,
  FormControls,
  FormikNumericField,
} from 'components';
import { useCurrencies, usePrevious } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { ChangeDisputeAmountDto, PayinOrder } from 'types';
import { validationUtils } from 'utils';

type Values = ChangeDisputeAmountDto;

type Props = {
  open: boolean;
  order: PayinOrder;
  error?: AxiosError | null;
  onClose: CloseDialogFormikHandler<Values>;
};

export const ChangeDisputeAmountDialog: React.FC<Props> = ({
  open,
  order,
  error,
  onClose,
}) => {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.change_dispute_amount',
  });
  const prevOpen = usePrevious(open);
  const { getFiatCurrencySymbol } = useCurrencies();
  const [initialValues, setInitialValues] = useState<Values>({
    amount: order.amount,
  });

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        amount: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const currencySymbol = useMemo(
    () => getFiatCurrencySymbol(order?.fiatCurrencyId),
    [order, getFiatCurrencySymbol],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result.data?.formikHelpers.resetForm();
      }
      onClose(result);
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues({ amount: order.amount });
    }
  }, [open, prevOpen, order]);

  useEffect(() => {
    if (error) {
      formik.setErrors(validationUtils.getFormErrors(error));
    } else {
      formik.setErrors({});
    }
  }, [formik, error]);

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title')}
        data={{ values: formik.values, formikHelpers: formik }}
        onClose={handleClose}
      >
        <Form>
          <FormControls>
            <FormikNumericField
              name="amount"
              label={t('new_amount')}
              suffix={currencySymbol}
              allowNegative={false}
              required
              fullWidth
            />
          </FormControls>
        </Form>
      </Dialog>
    </FormikProvider>
  );
};
